import { type RouteObject } from 'react-router';
import { standaloneLayout } from '@/app/layouts';
import { ROUTES } from '@/shared/routing';
import { composeLoaders } from '../compose-loaders';
import type { NavigationGuards } from '../navigation.guard';
import { planGuard } from '../plan.guard';
import { tossPaymentGuard } from '../toss-payment.guard';

/** 구독 화면 — 발전소 목록(`/subscriptions` 인덱스) + 구독유형 선택(`/subscriptions/:plantId`). 웹 전용. */
export function createSubscriptionRoute(guards: NavigationGuards): RouteObject | null {
  if (import.meta.env.VITE_BUILD_TARGET !== 'web') return null;

  // funnel 공통 게이트 — 인증 실패는 `reason=subscription` 을 실어 로그인으로, 필수 약관 미동의는 동의
  // 온보딩으로 보낸다. 레이아웃 라우트에 한 번 걸고, 자체 loader 가 필요한 자식은 이걸 앞에 얹는다.
  // 결제 비활성(env)이면 **인증보다 먼저** 안내로 돌린다 — 닫힌 퍼널 때문에 로그인을 요구할 이유가 없다.
  const funnelGate = composeLoaders(
    tossPaymentGuard,
    guards.requireAuthForSubscription,
    guards.requireConsent,
  );

  return {
    element: standaloneLayout({ width: 'sm' }), // TODO: 라우트별 width 값 지정 ('sm'|'md'|'lg'|'full')
    path: ROUTES.SUBSCRIPTIONS,
    // 자식은 loader 를 갖지 않는다 — RR7 부모·자식 loader 병렬 실행이라 자식 fetch 가 먼저 새기 때문.
    loader: funnelGate,
    children: [
      // `/subscriptions` = 발전소 목록(인덱스). (구 `/subscriptions/list` 폐지)
      {
        index: true,
        lazy: {
          Component: () => import('@/pages/subscriptions/list').then((m) => m.SubscriptionListPage),
        },
      },
      // 결제 결과 화면(완료/실패) — `?state=` 로 메시지·구분 수신. static 이라 `:plantId` 보다 매칭 우선.
      {
        path: ROUTES.SUBSCRIPTIONS_RESULT,
        lazy: {
          Component: () => import('@/pages/subscriptions/result').then((m) => m.PaymentResultPage),
        },
      },
      // Toss 카드 등록 인증창 콜백(`:result`=success|fail) — 디스패처: 빌링키 발급 API 호출 후 분기.
      {
        path: ROUTES.SUBSCRIPTIONS_CALLBACK,
        lazy: {
          Component: () =>
            import('@/pages/subscriptions/callback').then((m) => m.SubscriptionCallbackPage),
        },
      },
      // 구독유형 선택 — `/subscriptions/:plantId`(path param, dynamic). planGuard = 발전소 존재 검증 + prefetch
      // (페이지가 캐시 재사용, 없는 plantId 는 목록으로).
      //
      // ⚠️ **여기만 `funnelGate` 를 앞에 다시 얹는다** — `planGuard` 는 데이터 fetch 라, 부모 게이트가
      // redirect 해도 병렬로 이미 발사된다(미인증·미동의 상태의 plant 조회 → 401 → 글로벌 `onAuthError`
      // 오발). 순서를 보장할 수 있는 건 같은 loader 안뿐이다. 부모와 중복 실행되지만 bootstrap 은 멱등,
      // `isAuthenticated()` 는 동기, consents 는 `ensureQueryData` 가 in-flight 를 dedupe → 비용 없음.
      {
        path: ROUTES.SUBSCRIPTIONS_PLAN,
        loader: composeLoaders(funnelGate, planGuard),
        lazy: {
          Component: () => import('@/pages/subscriptions/plan').then((m) => m.PlanSelectionPage),
        },
      },
    ],
  };
}
